'use client';

import {
  PhoneCall,
  PhoneOff,
  PhoneIncoming,
  Minus,
  Maximize2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ActiveCall } from '@/hooks/voice/webphone-types';
import { useRingtone } from './use-ringtone';
import { CustomerCard } from './customer-card';
import { isInternalExtension } from '@/hooks/voice/phone-format';

interface Props {
  call: ActiveCall;
  onAccept: () => void;
  onReject: () => void;
  /**
   * Encolhe o cartao pra faixa (ajuste pos-smoke do modo discreto, 26/08/2026).
   * Ausente = cartao inteiro, que e como os call sites antigos e os testes do
   * #402 continuam usando este componente.
   */
  minimized?: boolean;
  /** Ausente esconde o botao de minimizar/expandir por completo. */
  onToggleMinimize?: () => void;
  /**
   * Alca de arrasto. Ausente = cartao fixo (herda a posicao de quem o
   * renderiza). Vai na REGIAO de cabecalho, nunca no cartao inteiro: os
   * controles ficam de fora do gesto.
   */
  onHandlePointerDown?: (e: React.PointerEvent<HTMLElement>) => void;
}

/**
 * Medidas do CARTAO — exportadas porque quem o posiciona precisa prende-lo a
 * viewport ANTES de ele existir na tela (#441), e o teste afere "o cartao
 * inteiro cabe" com essa mesma conta.
 *
 * Largura: o `w-80` da raiz. Altura: ESTIMATIVA — o cartao cresce e encolhe
 * com o `CustomerCard` (nome do ERP, "ramal interno", skeleton) e nao da pra
 * medir antes de existir. Arredondada PRA CIMA de proposito: superestimar so
 * afasta um pouco mais da borda; subestimar deixa o topo cortado, que e
 * exatamente o defeito da #441.
 */
export const INCOMING_CARD_WIDTH_PX = 320; // w-80
export const INCOMING_CARD_HEIGHT_PX = 400;

/**
 * Medidas da FAIXA minimizada. Diferente das de cima, estas sao EXATAS: a
 * faixa recebe largura e altura por `style` a partir destas constantes, entao
 * nao existe classe do Tailwind pra sair de sincronia com o numero — o valor
 * aqui e o valor na tela.
 */
export const INCOMING_CARD_MINIMIZED_WIDTH_PX = 288;
export const INCOMING_CARD_MINIMIZED_HEIGHT_PX = 56;

/**
 * #402 — modo DISCRETO da chamada entrante.
 *
 * Irmão do `IncomingCallModal`, com a MESMA assinatura de props obrigatórias. O
 * esqueleto visual é cópia do `WebphonePanelActive` (mesma largura `w-80`,
 * mesmo card arredondado), de propósito: o `WebphonePanelActive` é o componente
 * vivo de chamada ativa/outbound e enfiar nele uma prop condicional nova
 * arriscaria mute/hold/DTMF/transferência por uma tela que nem é dele.
 *
 * O `webphone-sticky.tsx` renderiza este card DENTRO de um container
 * posicionado, então ele substitui a bolha enquanto toca — em vez de empilhar
 * dois elementos no mesmo canto.
 *
 * Minimizado (26/08/2026) o card vira uma faixa que MANTÉM Atender e Recusar:
 * encolher não pode virar "perdi a ligação". Os dois estados são o MESMO
 * componente montado, e não dois componentes que se revezam, por causa do
 * ringtone: alternar entre eles desmontaria e remontaria o `useRingtone`,
 * reiniciando a cadência da campainha a cada clique.
 *
 * Ringtone: mora aqui, igual ao modal. Os dois NUNCA podem montar juntos —
 * seriam dois `AudioContext` tocando. Quem garante é o branch exclusivo do
 * sticky.
 *
 * SEM captura global de teclado, ao contrário do `IncomingCallModal`
 * (#402 — spec `2026-08-25-lote-telefonia-chat-187-155-402-435-design.md`):
 * o modo discreto existe pra NÃO interromper. Um `keydown` de Enter capturado
 * em `window` enquanto o agente está noutra tela — sem ter percebido que a
 * bolha virou card — atenderia ou recusaria a ligação sem ele saber. É
 * exatamente o bug que a #402 nasceu pra matar, reintroduzido no modo que
 * deveria ser o mais silencioso. Aqui o agente só age clicando — e minimizar
 * não abriu exceção: a faixa também não tem atalho nenhum.
 */
export function IncomingCallCard({
  call,
  onAccept,
  onReject,
  minimized = false,
  onToggleMinimize,
  onHandlePointerDown,
}: Props) {
  // Toca enquanto o card está aberto (cadência 'incoming', default do hook).
  // Vale nos dois estados: minimizado continua sendo uma ligação tocando.
  useRingtone(true);

  const quemLiga = call.remoteName ?? call.remoteNumber;
  // Classes da alça de arrasto. Sem `onHandlePointerDown` o cursor continua o
  // normal — prometer arrasto onde não há é pior que não oferecer.
  const handleClasses = onHandlePointerDown
    ? 'cursor-grab active:cursor-grabbing select-none'
    : undefined;
  const handleStyle = onHandlePointerDown
    ? ({ touchAction: 'none' } as const)
    : undefined;

  if (minimized) {
    return (
      <div
        data-testid="incoming-call-card"
        data-minimized="true"
        style={{
          width: INCOMING_CARD_MINIMIZED_WIDTH_PX,
          height: INCOMING_CARD_MINIMIZED_HEIGHT_PX,
        }}
        className="flex items-center gap-1.5 rounded-full border bg-card px-2 shadow-2xl"
      >
        <div
          onPointerDown={onHandlePointerDown}
          style={handleStyle}
          className={cn('flex h-full min-w-0 flex-1 items-center gap-2 pl-1', handleClasses)}
          title={onHandlePointerDown ? 'Arraste pra mover' : undefined}
        >
          <span className="relative flex h-8 w-8 shrink-0 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
            <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary/20">
              <PhoneIncoming className="h-4 w-4 text-primary" />
            </span>
          </span>
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">
              Chamada recebida
            </span>
            <span className="block truncate text-sm font-medium leading-tight">
              {quemLiga}
            </span>
          </span>
        </div>

        {onToggleMinimize && (
          <button
            type="button"
            onClick={onToggleMinimize}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Expandir chamada"
            title="Expandir chamada"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
        <Button
          size="icon"
          onClick={onReject}
          className="h-9 w-9 shrink-0 rounded-full bg-red-600 hover:bg-red-700 text-white"
          aria-label="Recusar"
          title="Recusar"
        >
          <PhoneOff className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          onClick={onAccept}
          className="h-9 w-9 shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
          aria-label="Atender"
          title="Atender"
        >
          <PhoneCall className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="incoming-call-card"
      data-minimized="false"
      className="relative w-80 rounded-2xl bg-card border shadow-2xl overflow-hidden"
    >
      <div className="px-6 pt-6 pb-4 bg-gradient-to-b from-primary/15 to-transparent text-center">
        {onToggleMinimize && (
          <button
            type="button"
            onClick={onToggleMinimize}
            className="absolute top-3 right-3 rounded p-1 hover:bg-accent"
            aria-label="Minimizar chamada"
            title="Minimizar chamada"
          >
            <Minus className="h-4 w-4" />
          </button>
        )}

        {/* Alça de arrasto: só o topo. O CustomerCard fica de fora pra o nome e
            o documento do cliente continuarem selecionáveis com o mouse. */}
        <div
          onPointerDown={onHandlePointerDown}
          style={handleStyle}
          className={handleClasses}
          title={onHandlePointerDown ? 'Arraste pra mover' : undefined}
        >
          <div className="flex justify-center mb-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <div className="relative h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
                <PhoneIncoming className="h-8 w-8 text-primary" />
              </div>
            </div>
          </div>

          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
            Chamada recebida
          </p>
          <p className="font-semibold">{quemLiga}</p>
          {call.remoteName && (
            <p className="text-xs text-muted-foreground font-mono mt-0.5">
              {call.remoteNumber}
            </p>
          )}
        </div>

        {/* Só busca cliente no ERP pra número externo — ramal interno
            (ramal↔ramal) dá match espúrio no IXC e mostra cliente errado. */}
        <div className="mt-3 text-left">
          {isInternalExtension(call.remoteNumber) ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Ramal interno {call.remoteNumber}
            </p>
          ) : (
            <CustomerCard phone={call.remoteNumber} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 p-4">
        <Button
          onClick={onReject}
          className="h-12 bg-red-600 hover:bg-red-700 text-white"
        >
          <PhoneOff className="h-4 w-4 mr-2" /> Recusar
        </Button>
        <Button
          onClick={onAccept}
          className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <PhoneCall className="h-4 w-4 mr-2" /> Atender
        </Button>
      </div>
    </div>
  );
}
